import {
    BOOKING_COMPLETE_GA_CATEGORY,
    BOOKINGCOMPLETE_GA_CATEGORY,
    BOOKINGS_GA_CATEGORY,
    GaActionText,
} from './constants';

import bookingStateSelector from '../../selectors/BookingStateSelector';
import { updateGTMDataLayer } from './utils/updateGTMDataLayer';
import {
    createGaActionCreator,
    createGaActionCreatorWithLabel,
} from '../../utils';
import { allowGovernmentInsuranceSelector } from '../../../config/ab/selector';
import logToDataDog from '../../../config/logToDataDog';

const TRACK_MADE_IT_TO_BOOKING_COMPLETE =
    '@@complete/TRACK_MADE_IT_TO_BOOKING_COMPLETE';
const TRACK_CLICK_VIEW_APPOINTMENTS =
    '@@complete/TRACK_CLICK_VIEW_APPOINTMENTS';
const TRACK_CLICK_CALL = '@@complete/TRACK_CLICK_CALL';
const TRACK_CLICK_ADD_TO_CALENDAR = '@@complete/TRACK_CLICK_ADD_TO_CALENDAR';
const TRACK_CLICK_CHECK_IN = '@@complete/TRACK_CLICK_CHECK_IN';
const TRACK_CLICK_DOWNLOAD_APP = '@@complete/TRACK_CLICK_DOWNLOAD_APP';
const TRACK_INSURANCE_CAPTURE_TEXT_SENT =
    '@@complete/TRACK_INSURANCE_CAPTURE_TEXT_SENT';
const TRACK_BOOKED_APPT = '@@complete/TRACK_BOOKED_APPT';
const TRACK_BOOKED = '@@complete/TRACK_BOOKED';
const TRACK_BOOKED_COVID_VACCINATION_DOSE_1 =
    '@@complete/TRACK_BOOKED_COVID_VACCINATION_DOSE_1';
const TRACK_BOOKED_COVID_VACCINATION_DOSE_2 =
    '@@complete/TRACK_BOOKED_COVID_VACCINATION_DOSE_2';
const TRACK_CLICK_NEW_BOOKING = '@@complete/TRACK_CLICK_NEW_BOOKING';
const TRACK_VIEWED_RECOMMENDATIONS_MODAL =
    '@@complete/TRACK_VIEWED_RECOMMENDATIONS_MODAL';
const TRACK_CLICKED_RECOMMENDATIONS_MODAL =
    '@@complete/TRACK_CLICKED_RECOMMENDATIONS_MODAL';
const TRACK_APPOINTMENT_INSTRUCTIONS_SHOWN =
    '@@complete/TRACK_APPOINTMENT_INSTRUCTIONS_SHOWN';

export const BookingCompletePageActions = {
    TRACK_BOOKED_APPT,
    TRACK_BOOKED,
    TRACK_BOOKED_COVID_VACCINATION_DOSE_1,
    TRACK_BOOKED_COVID_VACCINATION_DOSE_2,
    TRACK_CLICK_ADD_TO_CALENDAR,
    TRACK_CLICK_CALL,
    TRACK_CLICK_CHECK_IN,
    TRACK_CLICK_DOWNLOAD_APP,
    TRACK_CLICK_NEW_BOOKING,
    TRACK_CLICK_VIEW_APPOINTMENTS,
    TRACK_CLICKED_RECOMMENDATIONS_MODAL,
    TRACK_INSURANCE_CAPTURE_TEXT_SENT,
    TRACK_MADE_IT_TO_BOOKING_COMPLETE,
    TRACK_VIEWED_RECOMMENDATIONS_MODAL,
};

const trackInsuranceCaptureTextSent = () =>
    createGaActionCreator(
        TRACK_INSURANCE_CAPTURE_TEXT_SENT,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.insuranceCaptureTextWasSent
    );

export const trackMadeItToBookingComplete = () => {
    return (dispatch, getState) => {
        const baseAction = createGaActionCreator(
            TRACK_MADE_IT_TO_BOOKING_COMPLETE,
            BOOKING_COMPLETE_GA_CATEGORY,
            GaActionText.trackMadeItToBookingComplete
        );

        dispatch(baseAction);

        const state = getState();

        const bookingState = bookingStateSelector(state);
        const allowGovernmentInsurance = allowGovernmentInsuranceSelector(
            state
        );
        updateGTMDataLayer(bookingState, allowGovernmentInsurance);
    };
};

export const trackClickViewAppointments = () =>
    createGaActionCreator(
        TRACK_CLICK_VIEW_APPOINTMENTS,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.trackClickViewAppointments
    );

export const trackViewedRecommendationsModal = ({
    recommendationType,
    isMobile,
    isBookable,
}) => {
    const type = recommendationType || 'Unknown Recommendation Type';
    const viewSize = isMobile ? 'Mobile' : 'Desktop';
    const bookableStatus = isBookable ? 'Bookable' : 'Already booked';

    const action = `Viewed recommendation modal - ${viewSize} - ${type} - ${bookableStatus}`;

    return createGaActionCreator(
        TRACK_VIEWED_RECOMMENDATIONS_MODAL,
        BOOKING_COMPLETE_GA_CATEGORY,
        action
    );
};

export const trackClickedRecommendationsModal = (
    recommendationType,
    isMobile
) => {
    const type = recommendationType || 'Unknown Recommendation Type';
    const viewSize = isMobile ? 'Mobile' : 'Desktop';

    const action = `Clicked book recommendation modal - ${viewSize} - ${type}`;

    return createGaActionCreator(
        TRACK_CLICKED_RECOMMENDATIONS_MODAL,
        BOOKING_COMPLETE_GA_CATEGORY,
        action
    );
};

export const trackClickCall = () =>
    createGaActionCreator(
        TRACK_CLICK_CALL,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.trackClickCall
    );

export const trackClickAddToCalendar = () =>
    createGaActionCreator(
        TRACK_CLICK_ADD_TO_CALENDAR,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.trackClickAddToCalendar
    );

export const trackClickCheckIn = () =>
    createGaActionCreator(
        TRACK_CLICK_CHECK_IN,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.trackClickCheckIn
    );

export const trackClickDownloadApp = () =>
    createGaActionCreator(
        TRACK_CLICK_DOWNLOAD_APP,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.trackClickDownloadApp
    );

export const trackBookedAppt = () =>
    createGaActionCreator(
        TRACK_BOOKED_APPT,
        BOOKINGS_GA_CATEGORY,
        GaActionText.trackBookedAppt
    );

export const trackBooked = () =>
    createGaActionCreator(
        TRACK_BOOKED,
        BOOKINGCOMPLETE_GA_CATEGORY,
        GaActionText.trackBooked
    );

export const trackBookedCovidVaccinationDose1 = directoryId => {
    logToDataDog({
        name: 'booking-web-app.booked-covid-vaccination-dose-1',
        type: 'increment',
        tags: [`directoryId:${directoryId}`],
    });

    return createGaActionCreatorWithLabel(
        TRACK_BOOKED_COVID_VACCINATION_DOSE_1,
        BOOKINGCOMPLETE_GA_CATEGORY,
        GaActionText.trackBookedCovidVaccinationDose1,
        JSON.stringify({ directoryId })
    );
};

export const trackBookedCovidVaccinationDose2 = directoryId => {
    logToDataDog({
        name: 'booking-web-app.booked-covid-vaccination-dose-2',
        type: 'increment',
        tags: [`directoryId:${directoryId}`],
    });

    return createGaActionCreatorWithLabel(
        TRACK_BOOKED_COVID_VACCINATION_DOSE_2,
        BOOKINGCOMPLETE_GA_CATEGORY,
        GaActionText.trackBookedCovidVaccinationDose2,
        JSON.stringify({ directoryId })
    );
};

export const trackClickNewBooking = (procedureId, specialtyId) =>
    createGaActionCreatorWithLabel(
        TRACK_CLICK_NEW_BOOKING,
        BOOKING_COMPLETE_GA_CATEGORY,
        GaActionText.trackClickNewBooking,
        JSON.stringify({
            procedure_id: procedureId,
            specialty_id: specialtyId,
        })
    );

export const trackAppointmentInstructionsShown = (procedureId, requestId) =>
    createGaActionCreatorWithLabel(
        TRACK_APPOINTMENT_INSTRUCTIONS_SHOWN,
        BOOKINGCOMPLETE_GA_CATEGORY,
        GaActionText.trackAppointmentInstructionsShown,
        JSON.stringify({ procedureId, requestId })
    );

export const BookingCompletePageActionCreators = {
    trackInsuranceCaptureTextSent,
    trackMadeItToBookingComplete,
    trackClickViewAppointments,
    trackClickCall,
    trackClickAddToCalendar,
    trackClickCheckIn,
    trackClickDownloadApp,
    trackClickNewBooking,
    trackBookedAppt,
    trackBooked,
    trackBookedCovidVaccinationDose1,
    trackBookedCovidVaccinationDose2,
    trackClickedRecommendationsModal,
    trackViewedRecommendationsModal,
    trackAppointmentInstructionsShown,
};
